import React, { useState } from "react";
import { ChevronDown, ChevronUp, Info } from "lucide-react";

// Styles are global and loaded centrally via src/styles/App.scss (house convention:
// components do not import their own .scss).

interface TestingGuideProps {
	title: string;
	/** One line: what this screen is for, in the words of someone who has not seen it. */
	summary: string;
	/** What to do, in order. Each step is one action and what should happen. */
	steps: React.ReactNode[];
	/** Things that look like failures and are not. The most useful part.  */
	notMistakes?: React.ReactNode[];
	/** Starts folded, for a panel that is reference rather than orientation. */
	startCollapsed?: boolean;
}

/**
 * A short, collapsible "what am I looking at" panel for a screen that is being tried
 * out rather than used. It exists so a reviewer can tell a real defect from a designed
 * behaviour without asking, which is most of what goes wrong in a first walkthrough.
 */
const TestingGuide: React.FC<TestingGuideProps> = ({ title, summary, steps, notMistakes, startCollapsed }) => {
	const [isOpen, setIsOpen] = useState(!startCollapsed);

	return (
		<section className={`tgd-panel ${isOpen ? "" : "tgd-panel-collapsed"}`}>
			<button type="button" className="tgd-header" onClick={() => setIsOpen(open => !open)}>
				<Info size={16} className="tgd-icon" />
				<span className="tgd-title">{title}</span>
				{isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
			</button>

			{isOpen && (
				<div className="tgd-body">
					<p className="tgd-summary">{summary}</p>

					<ol className="tgd-steps">
						{steps.map((step, index) => (
							<li key={index} className="tgd-step">
								{step}
							</li>
						))}
					</ol>

					{notMistakes && notMistakes.length > 0 && (
						<div className="tgd-expected">
							<h4 className="tgd-expected-title">Looks wrong, isn&apos;t</h4>
							<ul className="tgd-expected-list">
								{notMistakes.map((note, index) => (
									<li key={index}>{note}</li>
								))}
							</ul>
						</div>
					)}
				</div>
			)}
		</section>
	);
};

export default TestingGuide;
